import { useEffect, useRef } from 'react';
import { useAuth } from './auth-context';
import { useColorMode } from './color-mode';

/**
 * UserThemeSetter component
 * 
 * This component listens for changes in the authenticated user's data.
 * If the user has a preferred theme stored in Firestore, it applies it 
 * to the application using the color mode provider.
 * 
 * It only applies the theme once per user login to avoid fighting 
 * with manual theme changes.
 */
export const UserThemeSetter = () => {
    const { user, loading } = useAuth();
    const { setColorMode } = useColorMode();
    const hasAppliedTheme = useRef(false);
    const lastUserId = useRef<string | null>(null);

    useEffect(() => {
        if (loading) return;

        // Reset if user changed (logout/login)
        if (user?.nyssoguid !== lastUserId.current) {
            hasAppliedTheme.current = false;
            lastUserId.current = user?.nyssoguid || null;
        }

        // Apply user theme preference once when user is loaded
        if (user && user.theme && !hasAppliedTheme.current) {
            //console.log(`Applying initial user theme preference from Firestore: ${user.theme}`);
            setColorMode(user.theme);
            hasAppliedTheme.current = true;
        }
    }, [user, loading, setColorMode]);

    return null;
};
